import type { ReactNode } from 'react';
import { cva } from 'class-variance-authority';

import { cn } from '@shared/lib/utils';
import { StatusBadge, type StatusBadgeTone } from './status-badge';

export type TrendDirection = 'up' | 'down' | 'flat' | 'unknown';
export type TrendSentiment = StatusBadgeTone;

const directionIcons: Record<TrendDirection, string> = {
  up: '↑',
  down: '↓',
  flat: '→',
  unknown: '—',
};

const inlineToneVariants = cva('', {
  variants: {
    sentiment: {
      positive: 'text-[hsl(var(--status-positive-foreground))]',
      neutral: 'text-muted-foreground',
      warning: 'text-[hsl(var(--status-warning-foreground))]',
      negative: 'text-[hsl(var(--status-negative-foreground))]',
      informational: 'text-[hsl(var(--status-informational-foreground))]',
    },
  },
  defaultVariants: { sentiment: 'neutral' },
});

const inlineSizeVariants = cva('', {
  variants: {
    size: {
      sm: 'gap-1 text-xs',
      md: 'gap-1.5 text-sm',
    },
  },
  defaultVariants: { size: 'sm' },
});

export interface TrendIndicatorProps {
  accessibleLabel: string;
  appearance?: 'inline' | 'badge';
  className?: string;
  context?: ReactNode;
  direction: TrendDirection;
  sentiment?: TrendSentiment;
  sentimentLabel: string;
  size?: 'sm' | 'md';
  value: ReactNode;
  wrap?: 'nowrap' | 'wrap';
}

export function TrendIndicator({
  accessibleLabel,
  appearance = 'inline',
  className,
  context,
  direction,
  sentiment = 'neutral',
  sentimentLabel,
  size = 'sm',
  value,
  wrap = 'nowrap',
}: TrendIndicatorProps) {
  const content = (
    <>
      <span aria-hidden="true" className="shrink-0 leading-none">
        {directionIcons[direction]}
      </span>
      <span>{value}</span>
      {context ? <span>{context}</span> : null}
      <span className="font-normal">({sentimentLabel})</span>
    </>
  );

  if (appearance === 'badge') {
    return (
      <StatusBadge
        aria-label={accessibleLabel}
        className={className}
        data-trend-direction={direction}
        data-trend-sentiment={sentiment}
        size={size}
        tone={sentiment}
        wrap={wrap}
      >
        {content}
      </StatusBadge>
    );
  }

  return (
    <span
      aria-label={accessibleLabel}
      className={cn(
        'inline-flex max-w-full items-center align-middle font-medium',
        wrap === 'wrap' ? 'flex-wrap whitespace-normal' : 'whitespace-nowrap',
        inlineToneVariants({ sentiment }),
        inlineSizeVariants({ size }),
        className
      )}
      data-trend-direction={direction}
      data-trend-sentiment={sentiment}
    >
      {content}
    </span>
  );
}
